import { useEffect, useRef } from 'react';

import { useLocation } from '@remix-run/react';

/**
 * Clears leftover scroll lock styles from body after route changes.
 *
 * Headless UI Dialog sets inline styles on body for scroll locking,
 * but these can persist after client-side navigation, causing layout issues
 * (e.g., white space at the bottom of the page).
 *
 * This hook must be called in the root App component (not in modals) because
 * modal-level cleanup gets overwritten by Headless UI's restoration logic.
 *
 * @see https://github.com/tailwindlabs/headlessui/issues/2168 - Overflow state not restored
 * @see https://github.com/tailwindlabs/headlessui/issues/1319 - Scroll lock technique issues
 * @see https://github.com/tailwindlabs/headlessui/discussions/510 - No scroll lock control
 */
export function useScrollLockCleanup() {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // Only cleanup when the path actually changes (not on initial mount)
    if (prevPathRef.current !== location.pathname) {
      if (typeof document !== 'undefined') {
        document.body.style.height = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.touchAction = '';
      }
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);
}
